import React, { useEffect, useMemo, useState } from 'react';
import { ApiGroup, createGroup, getGroups, updateGroup, deleteGroup } from '../../services/GroupApi';
import { createContact, getContacts, ApiContact, updateContact, deleteContact } from '../../services/ContactApi';
import { ImportPhoneNumbersModal } from './ImportPhoneNumbersModal';

interface Group {
  id: string;
  name: string;
  type: string;
  description: string;
  contactCount: number;
  createdAt: string;
  customType?: string;
}

interface ContactCreationSummary {
  successful: string[];
  failed: { phone: string; error: string }[];
}

function getCurrentUserId(): string | null {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.id || null;
  } catch (_) {
    return null;
  }
}

export const MyGroupsPage: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isQuickActionOpen, setIsQuickActionOpen] = useState(false);
  const [groupType, setGroupType] = useState('marketing');
  const [customGroupType, setCustomGroupType] = useState('');
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rows, setRows] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmGroup, setDeleteConfirmGroup] = useState<Group | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    type: 'marketing',
    customType: '',
    description: ''
  });
  const [viewingGroupPhones, setViewingGroupPhones] = useState<Group | null>(null);
  const [groupPhoneNumbers, setGroupPhoneNumbers] = useState<string[]>([]);
  const [groupContacts, setGroupContacts] = useState<ApiContact[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [isSavingPhones, setIsSavingPhones] = useState(false);
  const [contactCreationSummary, setContactCreationSummary] = useState<ContactCreationSummary | null>(null);
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');
  const [exportConfirmGroup, setExportConfirmGroup] = useState<Group | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const dismissContactSummary = () => setContactCreationSummary(null);

  const getApiErrorMessage = (err: any): string => {
    if (!err) return 'Unknown error';
    const raw =
      typeof err === 'string'
        ? err
        : err?.message || err?.error || (typeof err === 'object' ? JSON.stringify(err) : String(err));
    if (!raw) return 'Unknown error';
    const trimmed = raw.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed?.message && typeof parsed.message === 'string') {
          return parsed.message.trim() || 'Unknown error';
        }
      } catch (_) {
        // ignore JSON parse issues
      }
    }
    return trimmed;
  };

  const getApiErrorDetails = (err: any) => {
    const message = getApiErrorMessage(err);
    return {
      message,
      isForbidden: message.toLowerCase().includes('forbidden')
    };
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedGroups(groups.map(group => group.id));
    } else {
      setSelectedGroups([]);
    }
  };

  const handleSelectGroup = (groupId: string) => {
    if (selectedGroups.includes(groupId)) {
      setSelectedGroups(selectedGroups.filter(id => id !== groupId));
    } else {
      setSelectedGroups([...selectedGroups, groupId]);
    }
  };

  const validatePhoneNumber = (number: string): boolean => {
    // Remove any spaces, dashes, or other formatting
    const cleaned = number.replace(/[\s\-\(\)]/g, '');
    
    // Check if it starts with 263 and is exactly 12 digits
    if (!cleaned.startsWith('263')) {
      return false;
    }
    
    // Check if it's exactly 12 digits
    if (cleaned.length !== 12 || !/^\d{12}$/.test(cleaned)) {
      return false;
    }
    
    return true;
  };

  const handleAddRecipient = () => {
    setPhoneNumberError('');
    
    if (!phoneNumber.trim()) {
      setPhoneNumberError('Please enter a phone number');
      return;
    }

    // Clean the phone number (remove spaces, dashes, etc.)
    const cleaned = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Validate the phone number
    if (!validatePhoneNumber(cleaned)) {
      setPhoneNumberError('Phone number must start with 263 and be exactly 12 digits (e.g., 263712345678)');
      return;
    }

    // Check for duplicates
    if (recipients.includes(cleaned)) {
      setPhoneNumberError('This number is already in the list');
      return;
    }

    // Add the cleaned number
    setRecipients([...recipients, cleaned]);
    setPhoneNumber('');
    setPhoneNumberError('');
  };

  const handleRemoveRecipient = (numberToRemove: string) => {
    setRecipients(recipients.filter(number => number !== numberToRemove));
  };

  const handleImportPhoneNumbers = (phoneNumbers: string[]) => {
    // Add imported numbers to recipients, avoiding duplicates
    const newNumbers = phoneNumbers.filter(num => !recipients.includes(num));
    setRecipients([...recipients, ...newNumbers]);
  };

  const handleEditGroup = (group: Group) => {
    setEditingGroup(group);
    setEditFormData({
      name: group.name,
      type: group.type,
      customType: group.customType || '',
      description: group.description || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGroup) return;

    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await updateGroup(editingGroup.id, {
        name: editFormData.name.trim(),
        type: editFormData.type,
        description: editFormData.description.trim() || undefined,
        customType: editFormData.type === 'other' ? (editFormData.customType.trim() || undefined) : undefined
      });
      setSuccess('Group updated successfully');
      setIsEditModalOpen(false);
      setEditingGroup(null);
      setReloadSignal(Date.now());
    } catch (err: any) {
      setError(err?.message || 'Failed to update group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteConfirmGroup) return;

    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await deleteGroup(deleteConfirmGroup.id);
      setSuccess('Group deleted successfully');
      setIsDeleteModalOpen(false);
      setDeleteConfirmGroup(null);
      setReloadSignal(Date.now());
    } catch (err: any) {
      setError(err?.message || 'Failed to delete group');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (group: Group) => {
    setDeleteConfirmGroup(group);
    setIsDeleteModalOpen(true);
  };

  const openExportModal = (group: Group) => {
    setExportConfirmGroup(group);
    setIsExportModalOpen(true);
  };

  const generateCSV = (contacts: ApiContact[]): string => {
    // CSV headers
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Group', 'Notes'];
    
    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCSV = (value: string | null | undefined): string => {
      if (!value) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Build CSV rows
    const rows = contacts.map(contact => [
      escapeCSV(contact.firstName),
      escapeCSV(contact.lastName),
      escapeCSV(contact.email),
      escapeCSV(contact.phone),
      escapeCSV(contact.group),
      escapeCSV(contact.notes)
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  };

  const handleExportGroup = async () => {
    if (!exportConfirmGroup) return;

    setIsExporting(true);
    setError(null);

    try {
      // Get current user ID and fetch contacts
      const userId = getCurrentUserId();
      const allContacts = await getContacts(userId ? { userId } : undefined);

      // Filter contacts by group name (case-insensitive)
      const groupNameNormalized = (exportConfirmGroup.name || '').trim().toLowerCase();
      const groupContacts = allContacts.filter(contact => {
        const contactGroup = (contact.group || '').trim().toLowerCase();
        return contactGroup === groupNameNormalized;
      });

      if (groupContacts.length === 0) {
        setError('No contacts found in this group to export.');
        setIsExporting(false);
        return;
      }

      // Generate CSV content
      const csvContent = generateCSV(groupContacts);

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportConfirmGroup.name.replace(/[^a-z0-9]/gi, '_')}_contacts.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Close modal and show success
      setIsExportModalOpen(false);
      setExportConfirmGroup(null);
      setSuccess(`Successfully exported ${groupContacts.length} contact(s) to CSV`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error exporting group:', err);
      setError(err?.message || 'Failed to export group. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddPhoneToGroup = async () => {
    if (!viewingGroupPhones || !newPhoneNumber.trim()) return;

    // Clean the phone number (remove spaces, dashes, etc.)
    const cleaned = newPhoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Validate the phone number
    if (!validatePhoneNumber(cleaned)) {
      setError('Phone number must start with 263 and be exactly 12 digits (e.g., 263712345678)');
      return;
    }

    const phone = cleaned;
    
    // Check if phone number already exists in the group
    if (groupPhoneNumbers.includes(phone)) {
      setError('This phone number is already in the group');
      return;
    }

    setIsSavingPhones(true);
    setError(null);

    try {
      // Check if contact with this phone already exists
      const userId = getCurrentUserId();
      const allContacts = await getContacts(userId ? { userId } : undefined);
      const existingContact = allContacts.find(c => c.phone === phone);

      const contactPayload: any = {
        firstName: 'Contact',
        lastName: phone,
        phone: phone,
        group: viewingGroupPhones.name,
        notes: `Added to group "${viewingGroupPhones.name}"`
      };

      if (existingContact) {
        try {
          // Update existing contact to add to this group
          await updateContact(existingContact.id, {
            group: viewingGroupPhones.name
          });
        } catch (err: any) {
          const { message, isForbidden } = getApiErrorDetails(err);
          if (isForbidden) {
            try {
              await createContact(contactPayload);
            } catch (createErr: any) {
              const { message: fallbackMessage } = getApiErrorDetails(createErr);
              throw new Error(fallbackMessage);
            }
          } else {
            throw new Error(message);
          }
        }
      } else {
        try {
          await createContact(contactPayload);
        } catch (createErr: any) {
          const { message } = getApiErrorDetails(createErr);
          throw new Error(message);
        }
      }

      // Reload the group contacts
      await handleViewGroupPhones(viewingGroupPhones);
      setNewPhoneNumber('');
      setSuccess('Phone number added successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error adding phone number:', err);
      setError(err?.message || 'Failed to add phone number. Please try again.');
    } finally {
      setIsSavingPhones(false);
    }
  };

  const handleRemovePhoneFromGroup = async (contactId: string) => {
    if (!viewingGroupPhones) return;

    setIsSavingPhones(true);
    setError(null);

    try {
      // Get the contact to check if it only belongs to this group
      const userId = getCurrentUserId();
      const allContacts = await getContacts(userId ? { userId } : undefined);
      const contact = allContacts.find(c => c.id === contactId);

      if (!contact) {
        setError('Contact not found');
        setIsSavingPhones(false);
        return;
      }

      // Check if contact belongs to multiple groups (by checking if group name matches exactly)
      if (contact.group === viewingGroupPhones.name) {
        // Remove from this group by setting group to null
        await updateContact(contactId, {
          group: null
        });
      }

      // Reload the group contacts
      await handleViewGroupPhones(viewingGroupPhones);
      setSuccess('Phone number removed from group');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error removing phone number:', err);
      setError(err?.message || 'Failed to remove phone number. Please try again.');
    } finally {
      setIsSavingPhones(false);
    }
  };

  const handleViewGroupPhones = async (group: Group) => {
    setViewingGroupPhones(group);
    setLoadingPhones(true);
    setGroupPhoneNumbers([]);
    setGroupContacts([]);
    setError(null);
    try {
      // Get current user ID and fetch only their contacts
      const userId = getCurrentUserId();
      
      // Add a small delay to ensure database consistency if contacts were just created
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const contacts = await getContacts(userId ? { userId } : undefined);
      
      if (!contacts || contacts.length === 0) {
        setGroupPhoneNumbers([]);
        setGroupContacts([]);
        setLoadingPhones(false);
        setError('No contacts found in the system.');
        return;
      }

      // Filter contacts by group name - use case-insensitive comparison and trim both sides
      const groupNameNormalized = (group.name || '').trim().toLowerCase();
      
      if (!groupNameNormalized) {
        setError('Group name is empty');
        setLoadingPhones(false);
        return;
      }

      // Get all unique groups from contacts for debugging
      const allGroups: string[] = [];
      const seenGroups: { [key: string]: boolean } = {};
      contacts.forEach(c => {
        if (c && c.group) {
          const groupName = String(c.group).trim();
          if (groupName && !seenGroups[groupName]) {
            seenGroups[groupName] = true;
            allGroups.push(groupName);
          }
        }
      });

      // Filter contacts by group name - normalize both sides for comparison
      const matchingContacts = contacts.filter(c => {
        if (!c || !c.group) return false;
        const contactGroup = String(c.group).trim().toLowerCase();
        return contactGroup === groupNameNormalized;
      });
      
      // Deduplicate by phone number - keep only the first occurrence of each phone number
      const seenPhones = new Set<string>();
      const uniqueContacts: ApiContact[] = [];
      const uniquePhones: string[] = [];
      
      matchingContacts.forEach(c => {
        const phone = (c.phone || '').trim();
        if (phone && phone.length > 0) {
          // Normalize phone number for comparison (remove spaces, dashes, etc.)
          const phoneNormalized = phone.replace(/[\s\-\(\)]/g, '');
          if (!seenPhones.has(phoneNormalized)) {
            seenPhones.add(phoneNormalized);
            uniqueContacts.push(c);
            uniquePhones.push(phone);
          }
        }
      });
      
      setGroupPhoneNumbers(uniquePhones);
      setGroupContacts(uniqueContacts);
      
      // Debug logging to help diagnose issues
      if (uniquePhones.length === 0) {
        console.log('Debug - Phone numbers not found:', {
          'Group name (normalized)': groupNameNormalized,
          'Group name (original)': group.name,
          'Total contacts': contacts.length,
          'Available groups': allGroups,
          'Available groups (normalized)': allGroups.map(g => g.trim().toLowerCase()),
          'Matching contacts found': matchingContacts.length,
          'Unique contacts after deduplication': uniqueContacts.length,
          'Sample contacts with groups': contacts
            .filter(c => c && c.group)
            .slice(0, 10)
            .map(c => ({ 
              phone: c.phone, 
              group: c.group, 
              groupNormalized: String(c.group).trim().toLowerCase() 
            }))
        });
        setError(`No phone numbers found for group "${group.name}". Available groups: ${allGroups.length > 0 ? allGroups.join(', ') : 'none'}`);
      } else {
        console.log(`Successfully loaded ${uniquePhones.length} phone number(s) for group "${group.name}"`);
      }
    } catch (err: any) {
      console.error('Error loading phone numbers:', err);
      setError(err?.message || 'Failed to load phone numbers. Please try again.');
    } finally {
      setLoadingPhones(false);
    }
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setContactCreationSummary(null);
    setSubmitting(true);
    try {
      // Create the group first
      const newGroup = await createGroup({
        name: groupName.trim(),
        type: groupType,
        description: description.trim() || undefined,
        customType: groupType === 'other' ? (customGroupType.trim() || undefined) : undefined
      });

      // Create contacts for each phone number if any were added
      let contactResults: Array<{ status: 'fulfilled' | 'rejected'; phone: string; error?: string }> = [];
      if (recipients.length > 0) {
        const userId = getCurrentUserId();
        const groupNameTrimmed = groupName.trim();
        const createdContactIds = new Set<string>();
        contactResults = [];
        
        // Deduplicate recipients array - normalize phone numbers and remove duplicates
        const uniqueRecipients = Array.from(new Set(
          recipients
            .map(phone => phone.trim())
            .filter(phone => phone.length > 0)
        ));
        
        // Process contacts sequentially to avoid race conditions
        for (const phoneTrimmed of uniqueRecipients) {
          try {
            // Fetch existing contacts for this specific phone number
            // We fetch fresh each time to catch contacts created in this batch
            const allContacts = await getContacts(userId ? { userId } : undefined);
            
            // Normalize phone comparison - trim and compare
            const existingContact = allContacts.find(c => {
              const contactPhone = (c.phone || '').trim();
              return contactPhone === phoneTrimmed && !createdContactIds.has(c.id);
            });

            const contactPayload = {
              firstName: 'Contact',
              lastName: phoneTrimmed,
              phone: phoneTrimmed,
              group: groupNameTrimmed,
              notes: `Added when creating group "${groupNameTrimmed}"`
            };
            
            if (existingContact) {
              try {
                // Update existing contact to add to this group
                await updateContact(existingContact.id, {
                  group: groupNameTrimmed
                });
                createdContactIds.add(existingContact.id);
                contactResults.push({ status: 'fulfilled', phone: phoneTrimmed });
                continue;
              } catch (err: any) {
                const { message, isForbidden } = getApiErrorDetails(err);
                if (isForbidden) {
                  try {
                    const newContact = await createContact(contactPayload as any);
                    createdContactIds.add(newContact.id);
                    contactResults.push({ status: 'fulfilled', phone: phoneTrimmed });
                    continue;
                  } catch (createErr: any) {
                    const { message: fallbackMessage } = getApiErrorDetails(createErr);
                    contactResults.push({ 
                      status: 'rejected', 
                      phone: phoneTrimmed, 
                      error: fallbackMessage 
                    });
                    continue;
                  }
                } else {
                  contactResults.push({ 
                    status: 'rejected', 
                    phone: phoneTrimmed, 
                    error: message 
                  });
                  continue;
                }
              }
            }

            try {
              const newContact = await createContact(contactPayload as any);
              createdContactIds.add(newContact.id);
              contactResults.push({ status: 'fulfilled', phone: phoneTrimmed });
            } catch (createErr: any) {
              const { message } = getApiErrorDetails(createErr);
              contactResults.push({ 
                status: 'rejected', 
                phone: phoneTrimmed, 
                error: message 
              });
            }
          } catch (err: any) {
            const { message } = getApiErrorDetails(err);
            console.error(`Failed to create/update contact for phone ${phoneTrimmed}:`, err);
            contactResults.push({ 
              status: 'rejected', 
              phone: phoneTrimmed, 
              error: message 
            });
          }
        }
        
        // Summarize results for UI
        const successfulPhones = contactResults
          .filter(r => r.status === 'fulfilled')
          .map(r => r.phone);
        const failedDetails = contactResults
          .filter(r => r.status === 'rejected')
          .map(r => ({ phone: r.phone, error: r.error || 'Unknown error' }));

        if (successfulPhones.length > 0 || failedDetails.length > 0) {
          setContactCreationSummary({
            successful: successfulPhones,
            failed: failedDetails
          });
        }

        if (failedDetails.length > 0) {
          const failureText = failedDetails
            .map(r => `${r.phone}: ${r.error}`)
            .join('; ');
          console.error('Some contacts failed to create:', failureText);
          setError(`${failedDetails.length} phone number${failedDetails.length === 1 ? '' : 's'} failed to save. Review the details below.`);
        } else {
          setError(null);
        }
        
        // Wait a bit longer to ensure database consistency
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Reload groups list to get updated contact counts
      // Use a small delay to ensure database consistency
      await new Promise(resolve => setTimeout(resolve, 300));
      setReloadSignal(Date.now());
      
      const actualCreated = contactResults.filter(r => r.status === 'fulfilled').length;
      const failedCount = contactResults.filter(r => r.status === 'rejected').length;
      if (failedCount > 0) {
        setSuccess(`Group created with ${actualCreated} phone number${actualCreated === 1 ? '' : 's'}, but ${failedCount} failed.`);
      } else {
        setSuccess(`Group created${actualCreated > 0 ? ` with ${actualCreated} contact(s)` : ''}`);
      }
      setGroupName('');
      setDescription('');
      setCustomGroupType('');
      setGroupType('marketing');
      setPhoneNumber('');
      setRecipients([]);
      // Show the groups list after successful creation
      setShowCreateForm(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to create group');
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const userId = getCurrentUserId();
        const list = await getGroups(userId ? { userId } : undefined);
        if (ignore) return;
        const mapped: Group[] = (list || []).map((g: ApiGroup) => ({
          id: g.id,
          name: g.name,
          type: g.type,
          customType: g.customType || undefined,
          description: g.description || '',
          contactCount: g.contactCount || 0,
          createdAt: g.createdAt
        }));
        setRows(mapped);
      } catch (e: any) {
        if (!ignore) setError(e?.message || 'Failed to load groups');
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [reloadSignal]);

  const groups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.type.toLowerCase().includes(q) ||
      (r.customType || '').toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);

  return (
    <div className="space-y-6">
      {showCreateForm ? (
        <>
          {/* Header */}
          <div className="bg-white border-b">
            <div className="px-6 py-4">
              <h1 className="text-xl font-bold">Create new group</h1>
            </div>
          </div>

          {/* Create Group Form */}
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow mb-8">
        <form onSubmit={onSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
          )}
          {success && (
            <div className="p-3 rounded bg-green-50 text-green-700 border border-green-200 text-sm">{success}</div>
          )}
          {contactCreationSummary && (
            <div
              className={`p-4 rounded border ${
                contactCreationSummary.failed.length > 0
                  ? 'border-red-200 bg-red-50'
                  : 'border-green-200 bg-green-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {contactCreationSummary.failed.length > 0
                      ? 'Some phone numbers could not be saved'
                      : 'All phone numbers saved successfully'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {contactCreationSummary.failed.length > 0
                      ? 'Review the details below before retrying the failed numbers.'
                      : 'Review the saved numbers below.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={dismissContactSummary}
                  className="text-gray-500 hover:text-gray-700 text-xs font-medium"
                >
                  Dismiss
                </button>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded bg-white/70 border border-gray-200 p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Saved ({contactCreationSummary.successful.length})
                  </p>
                  {contactCreationSummary.successful.length > 0 ? (
                    <ul className="space-y-1 max-h-32 overflow-y-auto text-xs font-mono text-gray-700">
                      {contactCreationSummary.successful.map((phone) => (
                        <li key={`success-${phone}`} className="flex items-center space-x-2">
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                          <span>{phone}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-500">No numbers saved.</p>
                  )}
                </div>
                <div className="rounded bg-white/70 border border-gray-200 p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Failed ({contactCreationSummary.failed.length})
                  </p>
                  {contactCreationSummary.failed.length > 0 ? (
                    <ul className="space-y-1 max-h-32 overflow-y-auto text-xs text-gray-700">
                      {contactCreationSummary.failed.map(({ phone, error }) => (
                        <li key={`failed-${phone}`} className="flex flex-col border-b border-gray-100 pb-1 last:border-b-0">
                          <span className="font-mono">{phone}</span>
                          <span className="text-[11px] text-red-600">{error}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-500">No failures.</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group name
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Type
            </label>
            <select
              value={groupType}
              onChange={(e) => setGroupType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            >
              <option value="marketing">Marketing</option>
              <option value="customer-service">Customer Service</option>
              <option value="internal">Internal</option>
              <option value="support">Support</option>
              <option value="sales">Sales</option>
              <option value="other">Other</option>
            </select>
            {groupType === 'other' && (
              <div className="mt-2">
                <input
                  type="text"
                  value={customGroupType}
                  onChange={(e) => setCustomGroupType(e.target.value)}
                  placeholder="Enter custom group type"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter group description"
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
          </div>

          {/* Phone Number Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Add phone numbers to group
            </label>
            <div className="flex space-x-2">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  setPhoneNumberError(''); // Clear error when user types
                }}
                placeholder="Phone number (e.g., 263712345678)"
                className={`flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent ${
                  phoneNumberError ? 'border-red-300' : 'border-gray-300'
                }`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRecipient();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddRecipient}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            {phoneNumberError && (
              <p className="mt-1 text-sm text-red-600">{phoneNumberError}</p>
            )}
          </div>

          {/* Recipients List Box */}
          <div className="mt-4 border border-gray-200 rounded-md bg-white p-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">Phone Numbers</h3>
              <span className="text-sm text-gray-500">{recipients.length} number(s)</span>
            </div>
            {recipients.length > 0 ? (
              <div className="space-y-2">
                {recipients.slice(0, 5).map((number, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between bg-gray-50 p-2 rounded-md"
                  >
                    <span className="text-sm text-gray-600">{number}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipient(number)}
                      className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                {recipients.length > 5 && (
                  <div className="text-center py-2 text-sm text-gray-500 italic">
                    ... and {recipients.length - 5} more number(s)
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-gray-500">
                No phone numbers added yet
              </div>
            )}
          </div>

          {/* Import Phone Numbers Button */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setIsImportModalOpen(true)}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-[#FF5722] hover:text-[#FF5722] transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm font-medium">Import phone numbers from CSV/Excel</span>
            </button>
          </div>

          <div className="flex justify-between pt-4">
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="bg-gray-100 text-gray-600 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors duration-200"
            >
              Back to Groups List
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`bg-[#FF5722] text-white px-6 py-2 rounded-md transition-colors duration-200 ${submitting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#F4511E]'}`}
            >
              {submitting ? 'Creating...' : 'Create group'}
            </button>
          </div>
        </form>
      </div>
        </>
      ) : (
        <>
          {/* Groups List Banner */}
          <div className="bg-white border-b">
            <div className="px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Groups List</h2>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>New Group</span>
              </button>
            </div>
          </div>

          {/* Groups List Section */}
          <div className="space-y-6 rounded-lg p-6">
        {/* Actions Row */}
        <div className="flex justify-between items-center">
          {/* Quick Action Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsQuickActionOpen(!isQuickActionOpen)}
              className="flex items-center space-x-2 bg-white border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
            >
              <span>Quick action</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isQuickActionOpen && (
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                <div className="py-1">
                  <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Delete selected
                  </button>
                  <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Merge groups
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Search Box */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search group"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
            <svg 
              className="w-4 h-4 text-gray-400 absolute left-3 top-3"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Column Headers */}
        <div className="bg-white border rounded-md">
          <div className="grid grid-cols-[40px_2fr_1fr_3fr_1fr_1fr_100px] items-center border-b">
            <div className="py-3 px-4">
              <input
                type="checkbox"
                checked={selectedGroups.length === groups.length}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
              />
            </div>
            <div className="py-3 text-sm font-medium text-gray-700">Name</div>
            <div className="py-3 text-sm font-medium text-gray-700">Group Type</div>
            <div className="py-3 text-sm font-medium text-gray-700">Description</div>
            <div className="py-3 text-sm font-medium text-gray-700">Phone Numbers</div>
            <div className="py-3 text-sm font-medium text-gray-700">Created</div>
            <div className="py-3 text-sm font-medium text-gray-700">Actions</div>
          </div>
        </div>

        {/* Groups Table or Empty State */}
        {loading ? (
          <div className="text-sm text-gray-500">Loading groups...</div>
        ) : groups.length > 0 ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(group.id)}
                        onChange={() => handleSelectGroup(group.id)}
                        className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                      />
                    </td>
                    <td className="py-3 px-4">{group.name}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {group.type === 'other' ? group.customType || 'Other' : group.type}
                      </span>
                    </td>
                    <td className="py-3 px-4">{group.description}</td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleViewGroupPhones(group)}
                        className="flex items-center space-x-1 text-[#FF5722] hover:text-[#F4511E] transition-colors"
                        title={`View ${group.contactCount} phone number(s) in this group`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-sm font-medium">{group.contactCount}</span>
                        <span className="text-xs text-gray-500">phone{group.contactCount !== 1 ? 's' : ''}</span>
                      </button>
                    </td>
                    <td className="py-3 px-4">{group.createdAt}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditGroup(group)}
                          className="text-[#FF5722] hover:text-[#F4511E] transition-colors"
                          title="Edit group"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openExportModal(group)}
                          className="text-[#FF5722] hover:text-[#F4511E] transition-colors"
                          title="Export group as CSV"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openDeleteModal(group)}
                          className="text-red-600 hover:text-red-700 transition-colors"
                          title="Delete group"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="flex items-center space-x-2">
                <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-gray-600">0 - 0 of 0</span>
                <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Show rows</span>
                <select className="p-1 text-sm border rounded">
                  <option>10</option>
                  <option>25</option>
                  <option>50</option>
                  <option>100</option>
                </select>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No groups created yet</h3>
            <p className="text-gray-500 mb-6">
              Create your first group to organize your contacts and send bulk messages efficiently.
            </p>
          </div>
        )}
      </div>
        </>
      )}

      {/* Import Phone Numbers Modal */}
      <ImportPhoneNumbersModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSave={handleImportPhoneNumbers}
      />

      {/* Edit Group Modal */}
      {isEditModalOpen && editingGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Edit Group</h2>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingGroup(null);
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdateGroup} className="space-y-6">
              {error && (
                <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
              )}
              {success && (
                <div className="p-3 rounded bg-green-50 text-green-700 border border-green-200 text-sm">{success}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group name
                </label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder="Enter group name"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Type
                </label>
                <select
                  value={editFormData.type}
                  onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                >
                  <option value="marketing">Marketing</option>
                  <option value="customer-service">Customer Service</option>
                  <option value="internal">Internal</option>
                  <option value="support">Support</option>
                  <option value="sales">Sales</option>
                  <option value="other">Other</option>
                </select>
                {editFormData.type === 'other' && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={editFormData.customType}
                      onChange={(e) => setEditFormData({ ...editFormData, customType: e.target.value })}
                      placeholder="Enter custom group type"
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  placeholder="Enter group description"
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingGroup(null);
                    setError(null);
                  }}
                  className="px-6 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`bg-[#FF5722] text-white px-6 py-2 rounded-md transition-colors duration-200 ${submitting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#F4511E]'}`}
                >
                  {submitting ? 'Updating...' : 'Update Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deleteConfirmGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Delete Group</h2>
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteConfirmGroup(null);
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
            )}

            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the group <strong>"{deleteConfirmGroup.name}"</strong>? This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteConfirmGroup(null);
                  setError(null);
                }}
                className="px-6 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGroup}
                disabled={submitting}
                className={`bg-red-600 text-white px-6 py-2 rounded-md transition-colors duration-200 ${submitting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-red-700'}`}
              >
                {submitting ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export CSV Confirmation Modal */}
      {isExportModalOpen && exportConfirmGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Export Group as CSV</h2>
              <button
                onClick={() => {
                  setIsExportModalOpen(false);
                  setExportConfirmGroup(null);
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
            )}

            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Do you want to download the contacts from <strong>"{exportConfirmGroup.name}"</strong> as a CSV file?
              </p>
              <p className="text-sm text-gray-500">
                The file will contain all contacts in this group and will be saved to your device.
              </p>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setIsExportModalOpen(false);
                  setExportConfirmGroup(null);
                  setError(null);
                }}
                disabled={isExporting}
                className="px-6 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleExportGroup}
                disabled={isExporting}
                className={`bg-[#FF5722] text-white px-6 py-2 rounded-md transition-colors duration-200 ${isExporting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#F4511E]'}`}
              >
                {isExporting ? 'Exporting...' : 'Download CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Group Phone Numbers Modal */}
      {viewingGroupPhones && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Phone Numbers in "{viewingGroupPhones.name}"
              </h2>
              <button
                onClick={() => {
                  setViewingGroupPhones(null);
                  setGroupPhoneNumbers([]);
                  setGroupContacts([]);
                  setNewPhoneNumber('');
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 rounded bg-green-50 text-green-700 border border-green-200 text-sm">
                {success}
              </div>
            )}

            {/* Add Phone Number Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Phone Number
              </label>
              <div className="flex space-x-2">
                <input
                  type="tel"
                  value={newPhoneNumber}
                  onChange={(e) => {
                    setNewPhoneNumber(e.target.value);
                    if (error) setError(null); // Clear error when user types
                  }}
                  placeholder="Phone number (e.g., 263712345678)"
                  className={`flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent ${
                    error && error.includes('must start with 263') ? 'border-red-300' : 'border-gray-300'
                  }`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddPhoneToGroup();
                    }
                  }}
                />
                <button
                  onClick={handleAddPhoneToGroup}
                  disabled={!newPhoneNumber.trim() || isSavingPhones}
                  className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>

            {loadingPhones ? (
              <div className="text-center py-8">
                <div className="text-sm text-gray-500">Loading phone numbers...</div>
              </div>
            ) : groupPhoneNumbers.length > 0 ? (
              <div className="space-y-2">
                <div className="mb-4 text-sm text-gray-600">
                  Found {groupPhoneNumbers.length} phone number{groupPhoneNumbers.length !== 1 ? 's' : ''}
                </div>
                <div className="border rounded-md p-4 max-h-96 overflow-y-auto bg-gray-50">
                  <div className="space-y-2">
                    {groupContacts.map((contact, index) => (
                      <div
                        key={contact.id || index}
                        className="flex items-center justify-between bg-white p-3 rounded-md border border-gray-200"
                      >
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span className="text-sm text-gray-700 font-mono">{contact.phone}</span>
                        </div>
                        <button
                          onClick={() => handleRemovePhoneFromGroup(contact.id)}
                          disabled={isSavingPhones}
                          className="text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                          title="Remove from group"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <p className="text-gray-500">No phone numbers found in this group</p>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => {
                  setViewingGroupPhones(null);
                  setGroupPhoneNumbers([]);
                  setGroupContacts([]);
                  setNewPhoneNumber('');
                  setError(null);
                  setSuccess(null);
                }}
                className="px-6 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors duration-200"
                disabled={isSavingPhones}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyGroupsPage; 